import { Stack } from '@mui/material';
import { ApolloError } from '@apollo/client';
import { useCallback } from 'react';

import {
  Table,
  TableContainer,
  TableHeader,
  Pagination,
  SearchFormProps,
  SearchForm,
  Button,
  LoadedContent,
  IPaginationProps,
  LinearProgress,
} from '@occc/ui-design-system';
import { Cpg, SubmissionGroupLocationFragment } from '@occc/graphql';
import { useBulkSelect } from '@occc-app/hooks/useBulkSelect';
import { pluralize } from '@occc-app/lib/strings';

import {
  ScanDataSubmissionGroupDetailsLocationsTable,
  ScanDataSubmissionGroupDetailsLocationsTableProps,
} from './scan-data-submission-group-details-locations-table.component';

type ScanDataSubmissionGroupDetailsLocationsTableContainerProps = {
  cpg?: Cpg;
  data?: SubmissionGroupLocationFragment[];
  loading: boolean;
  error: ApolloError | undefined;
  onSearchSubmit: SearchFormProps['onSubmit'];
  search?: string;
  onAdd: () => void;
  onRemove: () => void;
  onRemoveSelected: () => void;
  paginationProps: IPaginationProps;
  canAddRemoveLocations?: boolean;
  isRemoveMode: boolean;
  onRemoveModeChange: (isRemoveMode: boolean) => void;
} & ReturnType<typeof useBulkSelect>;

export const ScanDataSubmissionGroupDetailsLocationsTableContainer = ({
  cpg,
  data,
  error,
  loading,
  onSearchSubmit,
  search,
  onAdd,
  onRemove,
  onRemoveSelected,
  paginationProps,
  canAddRemoveLocations = true,
  isRemoveMode,
  bulkSelectedItems,
  handleBulkSelectMultiple,
  handleBulkDeselectAll,
  handleItemSelect,
  onRemoveModeChange,
}: ScanDataSubmissionGroupDetailsLocationsTableContainerProps) => {
  const selectedCount = bulkSelectedItems.size;

  const formatBulkSelectionLabel = useCallback(
    () => `${selectedCount} ${pluralize(selectedCount, 'Location')} Selected`,
    [selectedCount],
  );

  const bulkActions = (
    <Stack direction='row' spacing={1}>
      <SearchForm onSubmit={onSearchSubmit} />
      <Button
        variant='text'
        onClick={() => {
          handleBulkDeselectAll();
          onRemoveModeChange(false);
        }}
      >
        Cancel
      </Button>
      {bulkSelectedItems.size > 0 && <Button onClick={onRemoveSelected}>Remove Selected</Button>}
    </Stack>
  );

  const groupLocationsTableProps: ScanDataSubmissionGroupDetailsLocationsTableProps = isRemoveMode
    ? {
        isRemoveMode: true,
        cpg,
        data,
        bulkSelectedItems,
        handleBulkSelectMultiple,
        handleBulkDeselectAll,
        handleItemSelect,
      }
    : { isRemoveMode: false, cpg, data };

  const renderData = () => {
    return (
      <>
        <Table sx={{ tableLayout: 'fixed' }}>
          <ScanDataSubmissionGroupDetailsLocationsTable {...groupLocationsTableProps} />
        </Table>
        <Pagination {...paginationProps} />
      </>
    );
  };

  return (
    <TableContainer>
      <TableHeader
        title='Group Locations'
        actions={
          <Stack direction='row' spacing={1}>
            <SearchForm onSubmit={onSearchSubmit} initialValues={{ search }} />
            {canAddRemoveLocations && !isRemoveMode && (
              <>
                <Button variant='contained' onClick={onAdd}>
                  Add
                </Button>
                {!!data?.length && (
                  <Button variant='contained' onClick={onRemove}>
                    Remove
                  </Button>
                )}
              </>
            )}
            {canAddRemoveLocations && isRemoveMode && (
              <Button
                variant='text'
                onClick={() => {
                  handleBulkDeselectAll();
                  onRemoveModeChange(false);
                }}
              >
                Cancel
              </Button>
            )}
          </Stack>
        }
        bulkSelected={isRemoveMode ? bulkSelectedItems.size : undefined}
        bulkActions={isRemoveMode ? bulkActions : undefined}
        formatBulkSelectionLabel={formatBulkSelectionLabel}
      />

      {loading && <LinearProgress />}

      <LoadedContent<SubmissionGroupLocationFragment>
        error={error}
        loading={loading}
        items={data}
        renderData={renderData}
      />
    </TableContainer>
  );
};
